import type { MediaEntityInfo } from "@/types/api";
import { isOmniEditVideoModel } from "@/utils/videoExtend";

function readParentVideoId(info: MediaEntityInfo | undefined): string | null {
  const id = info?.parent_video_id ?? info?.parentVideoId;
  const trimmed = typeof id === "string" ? id.trim() : "";
  return trimmed !== "" ? trimmed : null;
}

function readParentImageIds(info: MediaEntityInfo | undefined): readonly string[] {
  const ids = info?.parent_ids ?? info?.parentIds;
  if (ids === undefined || ids.length === 0) {
    return [];
  }

  return ids.filter((id): id is string => typeof id === "string" && id.trim() !== "");
}

/** Source video + reference image ids from persisted video entity metadata. */
export function partitionVideoEntityParents(
  info: MediaEntityInfo | undefined,
  projectVideoIds: readonly string[] = []
): { readonly parentVideoId: string | null; readonly parentImageIds: readonly string[] } {
  const explicitParentVideoId = readParentVideoId(info);
  const parentImageIds = readParentImageIds(info);

  if (explicitParentVideoId !== null) {
    return {
      parentVideoId: explicitParentVideoId,
      parentImageIds: parentImageIds.filter((id) => id !== explicitParentVideoId)
    };
  }

  // Legacy Omni Edit videos stored the source clip as parent_ids[0].
  const videoIdSet = new Set(projectVideoIds.map((id) => id.trim()).filter((id) => id !== ""));
  if (isOmniEditVideoModel(info?.source) && parentImageIds.length > 0) {
    const legacyParentVideoId = parentImageIds[0]!;
    if (videoIdSet.has(legacyParentVideoId)) {
      return {
        parentVideoId: legacyParentVideoId,
        parentImageIds: parentImageIds.slice(1)
      };
    }
  }

  return { parentVideoId: null, parentImageIds };
}

/** Reference ids for Video panel / metadata hydration: `[parent_video_id?, ...parent_image_ids]`. */
export function videoMetadataReferenceIds(
  info: MediaEntityInfo | undefined,
  projectVideoIds: readonly string[] = []
): readonly string[] {
  const { parentVideoId, parentImageIds } = partitionVideoEntityParents(info, projectVideoIds);
  if (parentVideoId !== null) {
    return [parentVideoId, ...parentImageIds];
  }

  return parentImageIds;
}
